const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.8;
const TARGET_SIZE = 5 * 1024 * 1024;
const JPEG_QUALITIES = [JPEG_QUALITY, 0.7, 0.6];
const ANIMATION_SCAN_BYTES = 1024 * 1024;

// Share the work when the same File is sent to several rooms at once (mass
// messaging), while still allowing a later retry after a transient failure.
const processingFiles = new WeakMap<File, Promise<File>>();

const isJpeg = (file: File) =>
  ['image/jpeg', 'image/jpg'].includes(file.type.toLowerCase()) || /\.(jpe?g)$/i.test(file.name);

const isHeic = (file: File) =>
  ['image/heic', 'image/heif'].includes(file.type.toLowerCase()) || /\.(heic|heif)$/i.test(file.name);

const isAvif = (file: File) => file.type.toLowerCase() === 'image/avif' || /\.avif$/i.test(file.name);

const isPng = (file: File) => file.type.toLowerCase() === 'image/png' || /\.png$/i.test(file.name);

const isWebp = (file: File) => file.type.toLowerCase() === 'image/webp' || /\.webp$/i.test(file.name);

const isBmp = (file: File) =>
  ['image/bmp', 'image/x-bmp', 'image/x-ms-bmp'].includes(file.type.toLowerCase()) || /\.bmp$/i.test(file.name);

const getDimensions = (image: ImageBitmap | HTMLImageElement) =>
  image instanceof HTMLImageElement
    ? { width: image.naturalWidth, height: image.naturalHeight }
    : { width: image.width, height: image.height };

async function decodeImage(blob: Blob): Promise<{ image: ImageBitmap | HTMLImageElement; cleanup?: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      return { image: await createImageBitmap(blob, { imageOrientation: 'from-image' }) };
    } catch {
      // Fall back to an HTML image for browsers without full ImageBitmap support.
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Image decoding failed'));
      image.src = url;
    });
    return { image, cleanup: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function encodeCanvas(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob?.type === type ? blob : null), type, quality);
  });
}

async function encodeLossyWithinTarget(
  canvas: HTMLCanvasElement,
  type: 'image/jpeg' | 'image/webp'
): Promise<Blob | null> {
  let smallestBlob: Blob | null = null;
  for (const quality of JPEG_QUALITIES) {
    const blob = await encodeCanvas(canvas, type, quality);
    if (!blob) continue;
    if (!smallestBlob || blob.size < smallestBlob.size) smallestBlob = blob;
    if (blob.size <= TARGET_SIZE) return blob;
  }
  return smallestBlob;
}

function createImageFile(blob: Blob, original: File, extension: string): File {
  const basename = original.name.replace(/\.[^./\\]+$/, '') || 'image';
  return new File([blob], `${basename}.${extension}`, { type: blob.type, lastModified: original.lastModified });
}

function canvasHasTransparency(context: CanvasRenderingContext2D, width: number, height: number): boolean {
  const pixels = context.getImageData(0, 0, width, height).data;
  for (let index = 3; index < pixels.length; index += 4) {
    if (pixels[index] < 255) return true;
  }
  return false;
}

async function isAnimatedPng(file: File): Promise<boolean> {
  const bytes = new Uint8Array(await file.slice(0, ANIMATION_SCAN_BYTES).arrayBuffer());
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const length = (bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3];
    const chunk = String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7]);
    if (chunk === 'acTL') return true;
    if (chunk === 'IDAT' || length < 0) return false;
    offset += length + 12;
  }
  return false;
}

async function isAnimatedWebp(file: File): Promise<boolean> {
  const bytes = new Uint8Array(await file.slice(0, 21).arrayBuffer());
  const isExtendedWebp =
    bytes.length >= 21 && bytes[12] === 0x56 && bytes[13] === 0x50 && bytes[14] === 0x38 && bytes[15] === 0x58;
  return isExtendedWebp && (bytes[20] & 0x02) !== 0;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function logCompression(
  file: File,
  compressed: File,
  originalDimensions: { width: number; height: number },
  compressedDimensions: { width: number; height: number }
): void {
  const savedBytes = file.size - compressed.size;
  const percentage = file.size ? (savedBytes / file.size) * 100 : 0;
  const originalSize = formatSize(file.size);
  const compressedSize = formatSize(compressed.size);
  const savedSize = formatSize(savedBytes);
  const compressionPercentage = `${percentage.toFixed(1)}%`;
  const originalResolution = `${originalDimensions.width} × ${originalDimensions.height}`;
  const compressedResolution = `${compressedDimensions.width} × ${compressedDimensions.height}`;

  console.groupCollapsed(`[iDigest Chat] 图片压缩完成：${file.name}（节省 ${compressionPercentage}）`);
  console.table([
    {
      文件名: file.name,
      压缩前大小: originalSize,
      压缩后大小: compressedSize,
      节省大小: savedSize,
      压缩比例: compressionPercentage,
      压缩前分辨率: originalResolution,
      压缩后分辨率: compressedResolution
    }
  ]);
  console.info(
    `压缩结果：${originalSize} → ${compressedSize}，节省 ${savedSize}（${compressionPercentage}）；分辨率：${originalResolution} → ${compressedResolution}`
  );
  console.groupEnd();
}

/**
 * Downscales common large raster images. Animated PNG/WebP files are kept
 * intact, and transparent PNG/WebP/AVIF files retain transparency. Any failure
 * or non-beneficial conversion returns the original upload unchanged.
 */
async function optimizeImage(file: File): Promise<File> {
  const heic = isHeic(file);
  const png = isPng(file);
  const webp = isWebp(file);
  const avif = isAvif(file);
  if (!isJpeg(file) && !heic && !avif && !png && !webp && !isBmp(file)) return file;
  if ((png && (await isAnimatedPng(file))) || (webp && (await isAnimatedWebp(file)))) return file;

  let source: Blob = file;
  if (heic) {
    const heic2any = (await import('heic2any')).default;
    const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: JPEG_QUALITY });
    source = Array.isArray(result) ? result[0] : result;
  }

  const { image, cleanup } = await decodeImage(source);
  try {
    const originalDimensions = getDimensions(image);
    const longestEdge = Math.max(originalDimensions.width, originalDimensions.height);
    if (!originalDimensions.width || !originalDimensions.height || longestEdge <= MAX_EDGE) return file;

    const scale = MAX_EDGE / longestEdge;
    const compressedDimensions = {
      width: Math.max(1, Math.round(originalDimensions.width * scale)),
      height: Math.max(1, Math.round(originalDimensions.height * scale))
    };
    const canvas = document.createElement('canvas');
    canvas.width = compressedDimensions.width;
    canvas.height = compressedDimensions.height;
    const context = canvas.getContext('2d');
    if (!context) return file;

    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const hasTransparency = (png || webp || avif) && canvasHasTransparency(context, canvas.width, canvas.height);
    const outputType = hasTransparency ? (png ? 'image/png' : 'image/webp') : 'image/jpeg';
    const blob =
      outputType === 'image/png'
        ? await encodeCanvas(canvas, outputType)
        : await encodeLossyWithinTarget(canvas, outputType);
    if (!blob || blob.size >= file.size) return file;

    const extension = outputType === 'image/png' ? 'png' : outputType === 'image/webp' ? 'webp' : 'jpg';
    const compressed = createImageFile(blob, file, extension);
    logCompression(file, compressed, originalDimensions, compressedDimensions);
    return compressed;
  } finally {
    if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) image.close();
    cleanup?.();
  }
}

export function processImageForUpload(file: File): Promise<File> {
  const existing = processingFiles.get(file);
  if (existing) return existing;

  const processing = optimizeImage(file)
    .then((result) => {
      // A compressed file can pass through another upload layer without being
      // encoded for a second time.
      if (result !== file) processingFiles.set(result, Promise.resolve(result));
      return result;
    })
    .catch((error) => {
      // Do not cache failures: retrying the same File should retry optimization.
      processingFiles.delete(file);
      console.warn('[iDigest Chat] Image optimization failed; uploading the original file', file.name, error);
      return file;
    });

  processingFiles.set(file, processing);
  return processing;
}
